import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import SplashScreen from "./components/SplashScreen";
import LoadingScreen from "./components/LoadingScreen";

// Retry interceptor for transient network / 5xx failures.
//
// Retrying WRITE requests (POST/PUT/PATCH/DELETE) is dangerous — the
// backend may have already committed the first attempt before the network
// / gateway timed out on the response. A blind retry then applies the
// same mutation a second time, producing doubled inserts (Excel imports
// with 1000+ rows hit this constantly because the proxy times out
// mid-response even though every row was already saved). So we only
// retry SAFE, idempotent methods: GET / HEAD / OPTIONS.
axios.interceptors.response.use(
  response => response,
  async (error) => {
    const config = error.config;
    const method = (config?.method || "get").toLowerCase();
    const isSafe = method === "get" || method === "head" || method === "options";
    if (isSafe && !config._retry && (!error.response || (error.response.status >= 500 && error.response.status < 600))) {
      config._retry = true;
      await new Promise(resolve => setTimeout(resolve, 1000));
      return axios(config);
    }
    return Promise.reject(error);
  }
);
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ItemMaster from "./pages/ItemMaster";
import PartMaster from "./pages/PartMaster";
import BOMMaster from "./pages/BOMMaster";
import ProcessMaster from "./pages/ProcessMaster";
import CustomerMaster from "./pages/CustomerMaster";
import CompanyMaster from "./pages/CompanyMaster";
import MachineMaster from "./pages/MachineMaster";
import ProductionPlanning from "./pages/ProductionPlanning";
import ItemMasterGuard from "./components/ItemMasterGuard";
import MasterGuard from "./components/MasterGuard";
import CostingGuard from "./components/CostingGuard";
import Operations from "./pages/Operations";
import FinalReport from "./pages/FinalReport";
import ExcelImport from "./pages/ExcelImport";
import StockAtSupplier from "./pages/StockAtSupplier";
import Suppliers from "./pages/Suppliers";
import RawMaterial from "./pages/RawMaterial";
import DailyReviewSheet from "./pages/DailyReviewSheet";
import SalesRegister from "./pages/SalesRegister";
import Settings from "./pages/Settings";
import DefectMaster from "./pages/DefectMaster";
import RMLog from "./pages/RMLog";
import RMPlanning from "./pages/RMPlanning";
import RMGradeStock from "./pages/RMGradeStock";
import RMOpeningStock from "./pages/RMOpeningStock";
import Inventory from "./pages/Inventory";
import Customization from "./pages/Customization";
import Setup from "./pages/Setup";
import JobWork from "./pages/JobWork";
import WorkOrder from "./pages/WorkOrder";
import BoughtOutMaster from "./pages/BoughtOutMaster";
import CostSheet from "./pages/CostSheet";
import CostSheetEditor from "./pages/CostSheetEditor";
import BudgetSchedule from "./pages/BudgetSchedule";
import PerformanceDispatch from "./pages/PerformanceDispatch";
import CustomerComplaints from "./pages/CustomerComplaints";
import QualityDashboard from "./pages/QualityDashboard";
import { MonthProvider } from "./contexts/MonthContext";
import { Toaster } from "@/components/ui/sonner";
import useHeartbeat from "./hooks/useHeartbeat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);
export const ProcessesContext = createContext(null);

// Legacy hardcoded fallbacks — kept so older imports still resolve while we
// migrate consumers to ProcessesContext (the dynamic per-tenant source of truth).
export const ALL_OPERATIONS = [
  "cutting", "incoming_store", "machining_p1", "machining_p2", "semi_firewall", "painting",
  "silver_plating", "welding", "outsource", "firewall", "dispatch"
];

export const SURFACE_PROCESSES = [
  "silver_plating", "anodising", "nickel_plating", "hardening",
  "nitriding", "zinc_plating", "phosphating"
];

export const OPERATION_NAMES = {
  cutting: "Cutting",
  incoming_store: "Incoming Store",
  machining_p1: "Machining P1",
  machining_p2: "Machining P2",
  semi_firewall: "Semi-Firewall",
  painting: "Painting",
  silver_plating: "Silver Plating",
  anodising: "Anodising",
  nickel_plating: "Nickel Plating",
  hardening: "Hardening",
  nitriding: "Nitriding",
  zinc_plating: "Zinc Plating",
  phosphating: "Phosphating",
  welding: "Welding",
  outsource: "Outsource",
  firewall: "Firewall",
  dispatch: "Dispatch"
};

/** Hook returning the live per-tenant processes from Customization → Processes. */
export function useProcesses() {
  return useContext(ProcessesContext) || {
    processes: [],
    inhouseProcesses: [],
    allOps: ALL_OPERATIONS,
    inhouseOps: ALL_OPERATIONS,
    opNames: OPERATION_NAMES,
    opPrefix: {},
    refresh: () => {},
  };
}

/** Provider that fetches /processes once on login and exposes the live list. */
function ProcessesProvider({ children }) {
  const [processes, setProcesses] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/processes`, { skipPeriod: true });
      const rows = (r.data || []).filter(p => p.enabled !== false);
      rows.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
      setProcesses(rows);
    } catch { /* keep stale */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) refresh();
    // Refresh whenever someone dispatches "processes:changed"
    const handler = () => refresh();
    window.addEventListener("processes:changed", handler);
    return () => window.removeEventListener("processes:changed", handler);
  }, [refresh]);

  const value = {
    processes,
    // "In-house only" subset — used by Reports/Final Report/Daily Review/Work
    // Order so outsourced processes don't clutter operational analytics.
    inhouseProcesses: processes.filter(p => p.is_inhouse === undefined ? true : !!p.is_inhouse),
    allOps: processes.length ? processes.map(p => p.code) : ALL_OPERATIONS,
    // In-house op codes (mirror of allOps but scoped) — same fallback.
    inhouseOps: processes.length
      ? processes.filter(p => p.is_inhouse === undefined ? true : !!p.is_inhouse).map(p => p.code)
      : ALL_OPERATIONS,
    opNames: processes.length
      ? Object.fromEntries(processes.map(p => [p.code, p.name || p.code.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())]))
      : OPERATION_NAMES,
    opPrefix: Object.fromEntries(processes.map(p => [p.code, p.prefix || p.code.slice(0, 2).toUpperCase()])),
    refresh,
  };

  return <ProcessesContext.Provider value={value}>{children}</ProcessesContext.Provider>;
}

function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" replace />;
}

/** Forces the user to /setup if their company needs first-time setup. */
function SetupGate({ needsSetup, children }) {
  if (needsSetup && window.location.pathname !== "/setup") {
    return <Navigate to="/setup" replace />;
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ssoExchanging, setSsoExchanging] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [publicNeedsSetup, setPublicNeedsSetup] = useState(false);

  useEffect(() => {
    // Step 0: Check public setup status FIRST — if the default company
    // hasn't been set up yet, the user lands on /setup before login.
    axios.get(`${API}/company/public-status`)
      .then(res => { if (res.data?.needs_setup) setPublicNeedsSetup(true); })
      .catch(() => {});

    // Step 1: Intercept ?sso_token=... from Swami Hub before anything else
    const url = new URL(window.location.href);
    const ssoToken = url.searchParams.get("sso_token");
    if (ssoToken) {
      // CRITICAL: wipe any stale session from a previous user on this browser
      // BEFORE doing anything else — prevents tenant leak.
      localStorage.removeItem("token");
      document.cookie = "access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      delete axios.defaults.headers.common["Authorization"];
      setSsoExchanging(true);
      axios.post(`${API}/swami-sso/exchange`, { sso_token: ssoToken })
        .then(res => {
          if (res.data?.token) {
            localStorage.setItem("token", res.data.token);
            axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
            setUser(res.data.user);
            setNeedsSetup(!!res.data.needs_setup);
          }
        })
        .catch(err => {
          console.error("Swami SSO failed:", err?.response?.data?.detail || err.message);
        })
        .finally(() => {
          url.searchParams.delete("sso_token");
          window.history.replaceState({}, "", url.toString());
          setSsoExchanging(false);
          setLoading(false);
        });
      return;
    }

    // Step 2: Standard local-token session restore
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(async res => {
          setUser(res.data);
          // Also check whether this company still needs setup
          try {
            const info = await axios.get(`${API}/company/info`);
            setNeedsSetup(!info.data?.setup_complete);
          } catch { /* non-fatal */ }
          setLoading(false);
        })
        .catch(() => { localStorage.removeItem("token"); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  const [showSplash, setShowSplash] = useState(!sessionStorage.getItem("splashSeen"));
  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem("splashSeen", "1");
    setShowSplash(false);
  }, []);

  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  if (ssoExchanging) {
    return <LoadingScreen label="Signing you in via Swami Hub…" />;
  }

  if (loading) {
    return <LoadingScreen label="Loading your factory…" />;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <MonthProvider>
      <ProcessesProvider>
      <div className="App">
        <BrowserRouter>
          {publicNeedsSetup ? (
            // Pre-login: first-run wizard. Blocks every other route including /login.
            <Routes>
              <Route path="/setup" element={<Setup publicMode onComplete={() => { setPublicNeedsSetup(false); window.location.replace("/login"); }} />} />
              <Route path="*" element={<Navigate to="/setup" replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/setup" element={<PrivateRoute><Setup onComplete={() => setNeedsSetup(false)} /></PrivateRoute>} />
              <Route path="/" element={<PrivateRoute><SetupGate needsSetup={needsSetup}>{user?.role === "Creator" ? <Settings /> : <Dashboard />}</SetupGate></PrivateRoute>} />
              <Route path="/items" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><ItemMasterGuard><ItemMaster /></ItemMasterGuard></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/bom-routing" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><ItemMasterGuard><ItemMaster /></ItemMasterGuard></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/part-master" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><PartMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/bom-master" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><BOMMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/process-master" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><ProcessMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/customer-master" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><CustomerMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/company-master" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><CompanyMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/machine-master" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><MachineMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/production-planning" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><ProductionPlanning /></SetupGate></PrivateRoute>} />
              <Route path="/operations/:type" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><Operations /></SetupGate></PrivateRoute>} />
              <Route path="/final-report" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><FinalReport /></SetupGate></PrivateRoute>} />
              <Route path="/excel-import" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><ExcelImport /></SetupGate></PrivateRoute>} />
              <Route path="/stock-at-supplier" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><StockAtSupplier /></SetupGate></PrivateRoute>} />
              <Route path="/suppliers" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><Suppliers /></SetupGate></PrivateRoute>} />
              <Route path="/raw-material" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><RawMaterial /></SetupGate></PrivateRoute>} />
              <Route path="/daily-review" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><DailyReviewSheet /></SetupGate></PrivateRoute>} />
              <Route path="/sales-register" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><SalesRegister /></SetupGate></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><Settings /></SetupGate></PrivateRoute>} />
              <Route path="/defect-master" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><DefectMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/rm-log" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><RMLog /></SetupGate></PrivateRoute>} />
              <Route path="/rm-planning" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><RMPlanning /></SetupGate></PrivateRoute>} />
              <Route path="/rm-grade-stock" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><RMGradeStock /></SetupGate></PrivateRoute>} />
              <Route path="/rm-opening-stock" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><RMOpeningStock /></SetupGate></PrivateRoute>} />
              <Route path="/inventory" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><Inventory /></SetupGate></PrivateRoute>} />
              <Route path="/customization" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><Customization /></SetupGate></PrivateRoute>} />
              <Route path="/job-work" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><JobWork /></SetupGate></PrivateRoute>} />
              <Route path="/work-order" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><WorkOrder /></SetupGate></PrivateRoute>} />
              <Route path="/work-order/:partId" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><WorkOrder /></SetupGate></PrivateRoute>} />
              <Route path="/bought-out" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><MasterGuard><BoughtOutMaster /></MasterGuard></SetupGate></PrivateRoute>} />
              <Route path="/cost-sheet" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><CostingGuard><CostSheet /></CostingGuard></SetupGate></PrivateRoute>} />
              <Route path="/cost-sheet/:id" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><CostingGuard><CostSheetEditor /></CostingGuard></SetupGate></PrivateRoute>} />
              <Route path="/budget-schedule" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><CostingGuard><BudgetSchedule /></CostingGuard></SetupGate></PrivateRoute>} />
              <Route path="/performance-dispatch" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><CostingGuard><PerformanceDispatch /></CostingGuard></SetupGate></PrivateRoute>} />
              <Route path="/customer-complaints" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><CustomerComplaints /></SetupGate></PrivateRoute>} />
              <Route path="/quality-dashboard" element={<PrivateRoute><SetupGate needsSetup={needsSetup}><QualityDashboard /></SetupGate></PrivateRoute>} />
            </Routes>
          )}
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
      </ProcessesProvider>
      </MonthProvider>
    </AuthContext.Provider>
  );
}

export default App;
